// import React from 'react'

// function VisitorApprovalDetails() {
//   return (
//     <div>VisitorApprovalDetails</div>
//   )
// }

// export default VisitorApprovalDetails

import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import swal from "sweetalert";
import {
  User,
  Calendar,
  Car,
  FileText,
  QrCode,
  Clock,
  ArrowLeft,
  CheckCircle,
  XCircle,
  AlertCircle,
  Mail,
  Building,
  MapPin,
  Users,
  IdCard,
  Globe,
} from "lucide-react";
import styles from "../../../Visitor Temporary GatePass/visitorGatePassDetails.module.css";

const VisitorApprovalDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const API = process.env.REACT_APP_API_URL;
  const AUTH_KEY = process.env.REACT_APP_AUTH_KEY;

  const [visitorPass, setVisitorPass] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [sendingPass, setSendingPass] = useState(false);

  const formatDate = (date) => {
    if (!date) return "-";
    return new Date(date).toLocaleString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "accept":
        return (
          <div className={`${styles.badge} ${styles.badgeAccepted}`}>
            <CheckCircle size={16} />
            <span>Accepted</span>
          </div>
        );
      case "reject":
        return (
          <div className={`${styles.badge} ${styles.badgeRejected}`}>
            <XCircle size={16} />
            <span>Rejected</span>
          </div>
        );
      default:
        return (
          <div className={`${styles.badge} ${styles.badgePending}`}>
            <AlertCircle size={16} />
            <span>Pending</span>
          </div>
        );
    }
  };

  const fetchVisitorPass = async () => {
    try {
      const response = await fetch(`${API}visitor-gatepass-details/${id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          authkey: AUTH_KEY,
        },
      });
      const data = await response.json();
      if (response.ok && data?.data?.visitorGatePass) {
        setVisitorPass(data.data.visitorGatePass);
      } else {
        toast.error("Failed to fetch visitor gate pass details");
        navigate(-1);
      }
    } catch (error) {
      toast.error("Something went wrong while fetching details");
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (action, fullName) => {
    const actionText = action === "accept" ? "Accept" : "Reject";

    const confirm = await swal({
      title: `${actionText} ${fullName}?`,
      text: `Are you sure you want to ${actionText.toLowerCase()} this Visitor pass?`,
      icon: action === "accept" ? "success" : "error",
      buttons: ["Cancel", actionText],
      dangerMode: action === "reject",
    });

    if (!confirm) return;

    try {
      setUpdatingStatus(true);
      const response = await fetch(
        `${API}update-visitor-gatepass-status/${visitorPass._id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            authkey: AUTH_KEY,
          },
          body: JSON.stringify({ status: action }),
        }
      );
      const data = await response.json();
      if (response.ok) {
        toast.success(`Pass ${action === "accept" ? "accepted" : "rejected"} successfully`);
        setVisitorPass((prev) => ({ ...prev, status: action }));
      } else {
        toast.error(data.message || "Failed to update status");
      }
    } catch (error) {
      toast.error("Error updating status");
    } finally {
      setUpdatingStatus(false);
    }
  };

  const sendGatePass = async () => {
    try {
      setSendingPass(true);
      const response = await fetch(`${API}send-visitor-gate-pass/${visitorPass._id}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          authkey: AUTH_KEY,
        },
      });
      const data = await response.json();
      if (response.ok) {
        toast.success(`Gate pass sent successfully`);
      } else {
        toast.error(data.message || "Failed to send gate pass");
      }
    } catch (error) {
      toast.error("Error sending gate pass");
    } finally {
      setSendingPass(false);
    }
  };

  useEffect(() => {
    fetchVisitorPass();
  }, [id, updatingStatus]);

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.spinner}></div>
      </div>
    );
  }

  if (!visitorPass) {
    return (
      <div className={styles.noData}>
        <p>No details found.</p>
      </div>
    );
  }

  const {
    visitors,
    vehicles,
    qrCode,
    scanHistory,
    status,
    createdAt,
    updatedAt,
    documents,
    companyName,
    email,
    visitorId,
    reasonOfVisit,
    visitingAreas,
    department,
    contactPerson,
    visitStart,
    visitEnd,
  } = visitorPass;

  return (
    <div className={styles.container}>
      {/* Header Section */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <div className={styles.avatar}>
            <User size={32} />
          </div>
          <div className={styles.headerInfo}>
            <h1 className={styles.title}>
              {visitors.map((v) => v.fullName).join(", ")}
            </h1>
            <div className={styles.metaInfo}>
              <span className={styles.metaItem}>
                <IdCard size={14} />
                {visitorId}
              </span>
              <span className={styles.metaItem}>
                <Building size={14} />
                {companyName || "N/A"}
              </span>
            </div>
          </div>
        </div>
        <div className={styles.headerRight}>{getStatusBadge(status)}</div>
      </div>

      {/* Visit Details Section */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <Calendar size={20} />
          <h2>Visit Details</h2>
        </div>
        <div className={styles.sectionContent}>
          <div className={styles.detailsGrid}>
            <div className={styles.detailItem}>
              <span className={styles.label}>Contact Person</span>
              <span className={styles.value}>{contactPerson || "-"}</span>
            </div>
            <div className={styles.detailItem}>
              <span className={styles.label}>Email</span>
              <span className={styles.value}>{email || "-"}</span>
            </div>
            <div className={styles.detailItem}>
              <span className={styles.label}>Department</span>
              <span className={styles.value}>{department?.name || "-"}</span>
            </div>
            <div className={styles.detailItem}>
              <span className={styles.label}>Reason of Visit</span>
              <span className={styles.value}>{reasonOfVisit || "-"}</span>
            </div>
            <div className={styles.detailItem}>
              <span className={styles.label}>Visiting Areas</span>
              <span className={styles.value}>{visitingAreas || "-"}</span>
            </div>
            <div className={styles.detailItem}>
              <span className={styles.label}>Visit Start</span>
              <span className={styles.value}>{formatDate(visitStart)}</span>
            </div>
            <div className={styles.detailItem}>
              <span className={styles.label}>Visit End</span>
              <span className={styles.value}>{formatDate(visitEnd)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Visitors Section */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <Users size={20} />
          <h2>Visitors ({visitors.length})</h2>
        </div>
        <div className={styles.sectionContent}>
          {visitors.map((visitor, index) => (
            <div key={visitor._id} className={styles.visitorCard}>
              <div className={styles.visitorHeader}>
                <div className={styles.visitorAvatar}>
                  {visitor.fullName.charAt(0).toUpperCase()}
                </div>
                <div className={styles.visitorInfo}>
                  <h3>{visitor.fullName}</h3>
                  <div className={styles.visitorMeta}>
                    <span>
                      <Globe size={14} />
                      {visitor.nationality}
                    </span>
                    <span>
                      <IdCard size={14} />
                      {visitor.idIqama}
                    </span>
                  </div>
                </div>
              </div>
              {visitor.nationalId?.url && (
                <div className={styles.documentLink}>
                  <FileText size={14} />
                  <a
                    href={visitor.nationalId.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View National ID
                  </a>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Vehicles Section */}
      {vehicles && vehicles.length > 0 && vehicles[0].plateNo && (
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <Car size={20} />
            <h2>Vehicles</h2>
          </div>
          <div className={styles.sectionContent}>
            {vehicles.map((vehicle, index) => (
              <div key={vehicle._id} className={styles.vehicleCard}>
                <div className={styles.detailsGrid}>
                  <div className={styles.detailItem}>
                    <span className={styles.label}>Vehicle Name</span>
                    <span className={styles.value}>{vehicle.name || "-"}</span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.label}>Model</span>
                    <span className={styles.value}>{vehicle.model || "-"}</span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.label}>Color</span>
                    <span className={styles.value}>{vehicle.color || "-"}</span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.label}>Plate No</span>
                    <span className={styles.value}>{vehicle.plateNo || "-"}</span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.label}>Owner</span>
                    <span className={styles.value}>{vehicle.ownerName || "-"}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Documents Section */}
      {documents && Object.values(documents).some((doc) => doc?.url) && (
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <FileText size={20} />
            <h2>Documents</h2>
          </div>
          <div className={styles.sectionContent}>
            <div className={styles.documentsGrid}>
              {Object.entries(documents).map(([key, doc]) => {
                if (!doc?.url) return null;
                return (
                  <div key={key} className={styles.documentItem}>
                    <FileText size={16} />
                    <div className={styles.documentInfo}>
                      <span className={styles.documentLabel}>
                        {key
                          .replace(/([A-Z])/g, " $1")
                          .replace(/^./, (str) => str.toUpperCase())}
                      </span>
                      <a
                        href={doc.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.documentLink}
                      >
                        {doc.originalFileName || "View Document"}
                      </a>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* QR Code Section */}
      {qrCode?.url && (
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <QrCode size={20} />
            <h2>Entry Pass QR Code</h2>
          </div>
          <div className={styles.sectionContent}>
            <div className={styles.qrCodeContainer}>
              <img src={qrCode.url} alt="QR Code" className={styles.qrCodeImage} />
              <p className={styles.qrCodeLabel}>Scan for Entry Verification</p>
            </div>
          </div>
        </div>
      )}

      {/* Scan History Section */}
      {scanHistory && scanHistory.length > 0 && (
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <Clock size={20} />
            <h2>Scan History</h2>
          </div>
          <div className={styles.sectionContent}>
            <div className={styles.tableWrapper}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Scan Time</th>
                    <th>Status Code</th>
                    <th>Validity</th>
                    <th>Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {scanHistory.map((scan) => (
                    <tr key={scan._id}>
                      <td>{formatDate(scan.scanTime)}</td>
                      <td>{scan.statusCode}</td>
                      <td>
                        {scan.isValid ? (
                          <span className={styles.validBadge}>Valid</span>
                        ) : (
                          <span className={styles.invalidBadge}>Invalid</span>
                        )}
                      </td>
                      <td>{scan.reason || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* System Info */}
      <div className={styles.systemInfo}>
        <div className={styles.systemInfoItem}>
          <span className={styles.label}>Created At:</span>
          <span className={styles.value}>{formatDate(createdAt)}</span>
        </div>
        <div className={styles.systemInfoItem}>
          <span className={styles.label}>Last Updated:</span>
          <span className={styles.value}>{formatDate(updatedAt)}</span>
        </div>
      </div>

      {/* Action Buttons */}
      <div className={styles.actions}>
        {/* {status === "pending" && (
          <>
            <button
              className={`${styles.btn} ${styles.btnAccept}`}
              disabled={updatingStatus}
              onClick={() =>
                updateStatus("accept", visitors.map((v) => v.fullName).join(", "))
              }
            >
              {updatingStatus ? (
                <div className={styles.btnSpinner}></div>
              ) : (
                <>
                  <CheckCircle size={18} />
                  Accept Pass
                </>
              )}
            </button>

            <button
              className={`${styles.btn} ${styles.btnReject}`}
              disabled={updatingStatus}
              onClick={() =>
                updateStatus("reject", visitors.map((v) => v.fullName).join(", "))
              }
            >
              {updatingStatus ? (
                <div className={styles.btnSpinner}></div>
              ) : (
                <>
                  <XCircle size={18} />
                  Reject Pass
                </>
              )}
            </button>
          </>
        )} */}
{/* 
        {status === "accept" && localStorage.getItem("userType") ==="Admin" && (
          <button
            className={`${styles.btn} ${styles.btnPrimary}`}
            disabled={sendingPass}
            onClick={sendGatePass}
          >
            {sendingPass ? (
              <div className={styles.btnSpinner}></div>
            ) : (
              <>
                <Mail size={18} />
                Send Gate Pass
              </>
            )}
          </button>
        )} */}

        <button
          className={`${styles.btn} ${styles.btnOutline}`}
          onClick={() => navigate(-1)}
        >
          <ArrowLeft size={18} />
          Back To List
        </button>
      </div>
    </div>
  );
};

export default VisitorApprovalDetails;